/**
 * frontend/src/crm/pages/Calendar.jsx — appels, rendez-vous, relances et tâches
 * du mois. Branche v4, handoff « Calendrier » : en-tête à sur-titre + navigateur
 * de mois, bandeau de 4 indicateurs, barre de vues (Mois / Agenda) et puces de
 * type, grille du mois ou agenda, panneau latéral du jour sélectionné.
 *
 * Données : GET /calendar (tâches + activités planifiées). Les polices suivent
 * la convention du dépôt (font-heading / font-mono) plutôt que les DM Sans /
 * DM Mono du prototype.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Users, Phone, AlertTriangle, Check, X } from "lucide-react";
import Modal from "@/crm/components/Modal";
import { ACTION_TYPES } from "@/crm/actions";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const WEEKDAYS_LONG = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const TYPES = {
  CALL: { label: "Appel", dot: "#3b82f6", tint: "#e6f1fb", fg: "#1f5f9e" },
  MEETING: { label: "Rendez-vous", dot: "#4a3ae0", tint: "#eeecff", fg: "#4a3ae0" },
  EMAIL: { label: "Relance", dot: "#2f9e5c", tint: "#e6f5ec", fg: "#1f7a48" },
  TASK: { label: "Tâche", dot: "#d0a02a", tint: "#fdf3dd", fg: "#8a6b1f" },
};
const TYPE_ORDER = ["CALL", "MEETING", "EMAIL", "TASK"];

const pad = (n) => String(n).padStart(2, "0");
const keyOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// Une entrée de /calendar est soit une tâche, soit une activité planifiée. Les
// tâches forment le type TASK quel que soit leur `type` métier ; une activité
// prend son type quand il correspond à une des trois colonnes de communication,
// et retombe sur TASK sinon pour ne jamais disparaître du calendrier.
const typeOf = (e) => (e.kind === "task" ? "TASK" : (TYPES[e.type] && e.type !== "TASK" ? e.type : "TASK"));

export default function Calendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [raw, setRaw] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState(today.getDate());
  const [view, setView] = useState("mois");
  const [commercial, setCommercial] = useState("");
  const [off, setOff] = useState({});
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/calendar").then(({ data }) => setRaw(data)).catch(() => setRaw([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.role !== "commercial") api.get("/users/commercials").then(({ data }) => setCommercials(data)).catch(() => {}); }, [user]);

  const todayKey = keyOf(today.getFullYear(), today.getMonth(), today.getDate());
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  // Évènements du mois affiché, normalisés puis triés par jour et par heure.
  const monthEvents = useMemo(() => {
    const prefix = `${year}-${pad(month + 1)}-`;
    return (raw || [])
      .filter((e) => (e.date || "").startsWith(prefix))
      .map((e) => {
        const ymd = (e.date || "").slice(0, 10);
        return {
          ...e,
          ymd,
          day: Number(ymd.slice(8, 10)),
          time: (e.date || "").length > 10 ? (e.date || "").slice(11, 16) : "",
          kindType: typeOf(e),
        };
      })
      .sort((a, b) => a.day - b.day || a.time.localeCompare(b.time));
  }, [raw, year, month]);

  // Puces de type et filtre commercial se cumulent, avant indicateurs et grille.
  const events = useMemo(
    () => monthEvents.filter((e) => !off[e.kindType]).filter((e) => !commercial || e.owner_id === commercial),
    [monthEvents, off, commercial],
  );

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.day] = m[e.day] || []).push(e); });
    return m;
  }, [events]);

  if (raw === null) return <Loader />;

  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth()); setSelected(1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); setSelected(today.getDate()); };

  const meetings = events.filter((e) => e.kindType === "MEETING").length;
  const calls = events.filter((e) => e.kindType === "CALL").length;
  const late = events.filter((e) => e.kindType === "TASK" && e.status !== "done" && isCurrentMonth && e.ymd < todayKey).length;

  const selEvents = byDay[selected] || [];
  const selWeekday = WEEKDAYS_LONG[(new Date(year, month, selected).getDay() + 6) % 7];
  const panelStats = TYPE_ORDER
    .map((k) => ({ k, n: selEvents.filter((e) => e.kindType === k).length }))
    .filter((x) => x.n > 0)
    .map((x) => ({
      dot: TYPES[x.k].dot,
      label: `${x.n} ${TYPES[x.k].label.toLowerCase()}${x.n > 1 && TYPES[x.k].label !== "Rendez-vous" ? "s" : ""}`,
    }));

  const agendaDays = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  const markDone = async (e) => {
    try { await api.put(`/tasks/${e.id}`, { status: "done" }); toast.success("Tâche terminée"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      {/* EN-TÊTE DE PAGE */}
      <div className="flex flex-wrap items-end justify-between gap-5 mb-[22px]">
        <div>
          <div className="font-mono text-[11px] tracking-[.14em] uppercase text-[#8a90a3] mb-2">Suivi</div>
          <h1 className="font-heading text-[30px] font-bold tracking-[-.02em] text-[#16181d]">Calendrier</h1>
          <p className="mt-2 text-[14.5px] text-[#61667a] max-w-[56ch] text-pretty">
            Appels, rendez-vous, relances et tâches. Cliquez sur un jour pour ouvrir son détail.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 max-[760px]:w-full">
          <div className="flex items-center gap-1 bg-white border border-[#e2e5ec] rounded-[11px] p-1">
            <button onClick={() => shiftMonth(-1)} title="Mois précédent"
              className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#f1f2f7]"><ChevronLeft size={16} className="text-[#3a3f52]" /></button>
            <span className="min-w-[158px] text-center text-sm font-bold tracking-[-.01em]">{MONTHS[month]} {year}</span>
            <button onClick={() => shiftMonth(1)} title="Mois suivant"
              className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#f1f2f7]"><ChevronRight size={16} className="text-[#3a3f52]" /></button>
          </div>
          <button onClick={goToday} className="bg-white border border-[#e2e5ec] rounded-[11px] px-3.5 py-2.5 text-[13.5px] font-medium hover:border-[#c9cedb]">Aujourd'hui</button>
          <button data-testid="calendar-new-event" onClick={() => setCreating(true)}
            className="flex items-center gap-2 bg-[#4a3ae0] border border-[#4a3ae0] text-white rounded-[11px] px-3.5 py-2.5 text-[13.5px] font-medium hover:bg-[#3d2fc9]">
            <Plus size={15} /> Nouvel évènement
          </button>
        </div>
      </div>

      {/* BANDEAU D'INDICATEURS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 min-[1025px]:grid-cols-4 gap-[14px] mb-5">
        <Kpi label="Évènements ce mois" value={events.length} tint="#eeecff" color="#4a3ae0" icon={CalendarDays} />
        <Kpi label="Rendez-vous" value={meetings} tint="#e6f1fb" color="#1f5f9e" icon={Users} />
        <Kpi label="Appels planifiés" value={calls} tint="#e6f5ec" color="#1f7a48" icon={Phone} />
        <Kpi label="Tâches en retard" value={late} tint="#fdf3dd" color="#a37a19" icon={AlertTriangle} />
      </div>

      {/* VUES & FILTRES */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-[3px] bg-white border border-[#e2e5ec] rounded-[11px] p-1">
          {[["mois", "Mois"], ["agenda", "Agenda"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-3.5 py-[7px] rounded-lg text-[13.5px] font-medium ${view === k ? "bg-[#14161f] text-white" : "text-[#61667a]"}`}>{label}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-[7px]">
          {TYPE_ORDER.map((k) => {
            const on = !off[k];
            const t = TYPES[k];
            return (
              <button key={k} onClick={() => setOff((s) => ({ ...s, [k]: on }))}
                className="flex items-center gap-[7px] border rounded-full px-3 py-[7px] text-[13px] font-medium"
                style={on
                  ? { background: t.tint, color: t.fg, borderColor: "transparent" }
                  : { background: "#fff", color: "#9aa0b2", borderColor: "#e2e5ec" }}>
                <span className="h-2 w-2 rounded-full" style={{ background: on ? t.dot : "#c9cedb" }} />
                <span>{t.label}</span>
                <span className="font-mono text-[11px] opacity-75">{monthEvents.filter((e) => e.kindType === k).length}</span>
              </button>
            );
          })}
        </div>
        <div className="flex-1" />
        {commercials.length > 0 && (
          <select data-testid="calendar-filter-comm" value={commercial} onChange={(e) => setCommercial(e.target.value)}
            className="border border-[#e2e5ec] bg-white rounded-[11px] px-3 py-2.5 text-[13.5px] cursor-pointer hover:border-[#c9cedb]">
            <option value="">Tous commerciaux</option>
            {commercials.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 min-[1281px]:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <div className="min-w-0">
          {view === "mois" ? (
            <div className="bg-white border border-[#e6e8ee] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-7 bg-[#f7f8fb] border-b border-[#e6e8ee]">
                {DAY_LABELS.map((d) => (
                  <div key={d} className="px-2 py-2.5 text-center font-mono text-[10.5px] tracking-[.1em] uppercase text-[#8a90a3]">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {Array.from({ length: totalCells }, (_, i) => {
                  const num = i - startOffset + 1;
                  const inMonth = num >= 1 && num <= daysInMonth;
                  const dayEvents = inMonth ? (byDay[num] || []) : [];
                  const isToday = inMonth && isCurrentMonth && num === today.getDate();
                  const isSel = inMonth && num === selected;
                  return (
                    <div key={i} onClick={() => inMonth && setSelected(num)}
                      className={`min-h-[116px] max-[760px]:min-h-[84px] border-b border-r border-[#eef0f4] p-2 flex flex-col gap-[5px] ${inMonth ? "cursor-pointer" : "cursor-default"}`}
                      style={{ background: !inMonth ? "#fafbfc" : isSel ? "#f4f2ff" : "#fff" }}>
                      <div className="flex items-center justify-between gap-1.5">
                        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full font-mono text-[11.5px] font-medium"
                          style={{
                            background: isToday ? "#4a3ae0" : isSel ? "#e6e2ff" : "transparent",
                            color: isToday ? "#fff" : inMonth ? "#61667a" : "#c9cedb",
                          }}>{inMonth ? num : ""}</span>
                        {dayEvents.length > 0 && <span className="font-mono text-[10.5px] text-[#9aa0b2]">{dayEvents.length}</span>}
                      </div>
                      {dayEvents.slice(0, 3).map((e) => (
                        <div key={e.id} data-testid="calendar-event"
                          onClick={(ev) => { ev.stopPropagation(); e.company_id && navigate(`/entreprises/${e.company_id}`); }}
                          className="flex items-center gap-1.5 rounded-[7px] px-[7px] py-[3px] text-[11.5px] overflow-hidden cursor-pointer"
                          style={{ background: TYPES[e.kindType].tint, color: TYPES[e.kindType].fg }}>
                          {e.time && <span className="shrink-0 font-mono text-[10.5px] opacity-80 max-[760px]:hidden">{e.time}</span>}
                          <span className="flex-1 min-w-0 truncate font-medium">{e.title}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && <div className="font-mono text-[10.5px] text-[#9aa0b2] pl-0.5">+ {dayEvents.length - 3}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#e6e8ee] rounded-2xl overflow-hidden">
              {agendaDays.map((d) => {
                const isToday = isCurrentMonth && d === today.getDate();
                return (
                  <div key={d} className="grid grid-cols-[132px_minmax(0,1fr)] border-b border-[#eef0f4]">
                    <div className="px-4 py-3.5 border-r border-[#eef0f4]" style={{ background: isToday ? "#f4f2ff" : "#fbfbfd" }}>
                      <div className="font-mono text-[11px] tracking-[.1em] uppercase text-[#9aa0b2]">
                        {WEEKDAYS_LONG[(new Date(year, month, d).getDay() + 6) % 7]}
                      </div>
                      <div className="text-[17px] font-bold tracking-[-.01em] mt-1" style={{ color: isToday ? "#4a3ae0" : "#16181d" }}>
                        {d} {MONTHS[month].toLowerCase()}
                      </div>
                    </div>
                    <div className="px-3.5 py-2.5 flex flex-col gap-2">
                      {byDay[d].map((e) => (
                        <div key={e.id} data-testid="calendar-event"
                          onClick={() => e.company_id && navigate(`/entreprises/${e.company_id}`)}
                          className="flex items-center gap-3 border border-[#eef0f4] rounded-[11px] px-3 py-[9px] hover:border-[#d7dae4] cursor-pointer">
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: TYPES[e.kindType].dot }} />
                          <span className="shrink-0 w-[42px] font-mono text-xs text-[#6f7488]">{e.time || "—"}</span>
                          <span className="flex-1 min-w-0 text-sm font-medium truncate">{e.title}</span>
                          <span className="shrink-0 max-w-[190px] text-[12.5px] text-[#8a90a3] truncate">{e.company_name || ""}</span>
                          <span className="shrink-0 font-mono text-[10.5px] rounded-md px-[7px] py-[3px]"
                            style={{ background: TYPES[e.kindType].tint, color: TYPES[e.kindType].fg }}>{TYPES[e.kindType].label}</span>
                          <span className="shrink-0 font-mono text-[10.5px] tracking-[.04em] text-[#9aa0b2]">{e.owner_name || ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {agendaDays.length === 0 && (
                <div className="px-5 py-[54px] text-center text-[13.5px] text-[#9aa0b2]">Aucun évènement pour ce mois avec ces filtres.</div>
              )}
            </div>
          )}
        </div>

        {/* PANNEAU DU JOUR */}
        <aside className="min-[1281px]:sticky min-[1281px]:top-[76px] bg-white border border-[#e6e8ee] rounded-2xl p-[18px] flex flex-col gap-3.5">
          <div>
            <div className="font-mono text-[10.5px] tracking-[.12em] uppercase text-[#8a90a3]">
              {isCurrentMonth && selected === today.getDate() ? "Aujourd'hui" : selWeekday}
            </div>
            <div className="text-[19px] font-bold tracking-[-.01em] mt-1.5">{selected} {MONTHS[month].toLowerCase()} {year}</div>
          </div>

          {panelStats.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {panelStats.map((s) => (
                <span key={s.label} className="flex items-center gap-1.5 text-[12.5px] text-[#61667a] bg-[#f7f8fb] rounded-full px-2.5 py-[5px]">
                  <span className="h-[7px] w-[7px] rounded-full" style={{ background: s.dot }} />{s.label}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-2.5">
            {selEvents.map((e) => (
              <article key={e.id} className="border border-[#eef0f4] rounded-[11px] px-3 py-[11px] flex flex-col gap-[7px] hover:border-[#d7dae4]"
                style={{ borderLeft: `3px solid ${TYPES[e.kindType].dot}` }}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-medium text-[#16181d]">{e.time || "—"}</span>
                  <span className="font-mono text-[10.5px] rounded-md px-[7px] py-0.5"
                    style={{ background: TYPES[e.kindType].tint, color: TYPES[e.kindType].fg }}>{TYPES[e.kindType].label}</span>
                </div>
                <div className="text-sm font-medium text-pretty">{e.title}</div>
                <div className="text-[12.5px] text-[#8a90a3] truncate">
                  {[e.company_name, e.owner_name].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="flex items-center gap-[7px]">
                  <button disabled={!e.company_id} onClick={() => navigate(`/entreprises/${e.company_id}`)}
                    className="flex-1 border border-[#e2e5ec] bg-white rounded-[9px] px-2.5 py-1.5 text-[12.5px] hover:border-[#c9cedb] disabled:opacity-40 disabled:cursor-not-allowed">
                    Ouvrir la fiche
                  </button>
                  {e.kind === "task" && e.status !== "done" && (
                    <button title="Marquer terminé" onClick={() => markDone(e)}
                      className="shrink-0 h-8 w-8 border border-[#e2e5ec] bg-white rounded-[9px] flex items-center justify-center hover:border-[#a9e0bf] hover:bg-[#f2fbf5]">
                      <Check size={15} className="text-[#2f9e5c]" />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>

          {selEvents.length === 0 && (
            <div className="border border-dashed border-[#d7dae4] rounded-xl px-3.5 py-[26px] text-center text-[13px] text-[#9aa0b2]">
              Rien de prévu ce jour.
            </div>
          )}
        </aside>
      </div>

      {creating && (
        <NewEventModal defaultDate={keyOf(year, month, selected)} onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function Kpi({ label, value, tint, color, icon: Icon }) {
  return (
    <div className="bg-white border border-[#e6e8ee] rounded-2xl px-[18px] py-4 flex items-center gap-[14px]">
      <div className="h-10 w-10 shrink-0 rounded-[11px] flex items-center justify-center" style={{ background: tint }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="font-mono text-[22px] font-medium tracking-[-.02em]">{value}</div>
        <div className="text-[12.5px] text-[#8a90a3] truncate">{label}</div>
      </div>
    </div>
  );
}

// « Nouvel évènement » : la seule mutation de création disponible côté API est
// la tâche planifiée (POST /tasks), qui remonte ensuite dans /calendar.
function NewEventModal({ defaultDate, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("FOLLOW_UP");
  const [due, setDue] = useState(defaultDate);
  const [priority, setPriority] = useState("medium");
  const [saving, setSaving] = useState(false);
  const FIELD = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-500";

  const save = async () => {
    if (!title.trim()) return toast.error("Titre requis");
    setSaving(true);
    try {
      await api.post("/tasks", { title: title.trim(), type, due_date: due || null, priority });
      toast.success("Évènement créé"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Modal onClose={onClose} className="max-w-[420px]" testid="calendar-event-modal" label="Nouvel évènement">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-[17px] text-slate-900">Nouvel évènement</h3>
        <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
      </div>
      <div className="mt-3.5 space-y-2">
        <input data-testid="calendar-event-title" placeholder="Intitulé de l'évènement" value={title}
          onChange={(e) => setTitle(e.target.value)} className={FIELD} />
        <div className="grid grid-cols-2 gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)} className={`${FIELD} bg-white text-slate-600`}>
            {ACTION_TYPES.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className={`${FIELD} bg-white text-slate-600`}>
            {[["low", "Basse"], ["medium", "Moyenne"], ["high", "Haute"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={`${FIELD} text-slate-600`} />
        <div className="flex gap-2 pt-1.5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:border-slate-300">Annuler</button>
          <button data-testid="calendar-event-save" onClick={save} disabled={saving}
            className="flex-1 py-2.5 px-3.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
            {saving ? "Création…" : "Créer"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
